<template>
  <div>
    <loading-spinner v-if="loading" class="mx-auto mt-16" :width="32" :height="32" />
    <div v-if="!loading" class="flex flex-col gap-16 mt-16">
      <p v-if="jobRecruitInfo.recruitedBySeeker" class="text-grey-text-subdued text-body italic">
        You've invited this sitter to apply to your job.
      </p>
      <poppins-button
        v-for="(button, index) in contactButtons"
        :key="index"
        class="w-full"
        size="medium"
        :value="button.value"
        :variant="button.variant"
        :href="button.href"
        :to="button.to"
        @click="handleButtonClick(button)"
      />
    </div>
  </div>
</template>

<script lang="ts">
import PoppinsButton from '@/components/button/Button.vue';
import { JOB_RECRUIT } from '@/graphql/provider/profile/queries';
import { isChildCare } from '../__types__/CareTypes';
import { sendSegmentEvent } from '@/lib/segment';
import LoadingSpinner from '@/components/spinner/Spinner.vue';

export default {
  name: 'ProviderProfileContactCtas',
  components: {
    PoppinsButton,
    LoadingSpinner,
  },
  props: {
    profileAccountData: {
      default: () => ({}),
      type: Object,
    },
    accountTypeInfo: {
      default: () => ({}),
      type: Object,
    },
    careType: {
      type: String,
      required: true,
    },
    showPrimaryOnly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      jobRecruitInfo: {} as any,
      loading: true,
    };
  },
  computed: {
    isPremiumParent(): boolean {
      return (
        this.accountTypeInfo?.isParent &&
        (this.accountTypeInfo?.accountType === 'premium' ||
          this.accountTypeInfo?.accountType === 'corporate' ||
          this.accountTypeInfo?.accountType === 'family_concierge')
      );
    },
    isBasicParent(): boolean {
      return this.accountTypeInfo.isParent && !this.isPremiumParent;
    },
    sitterCanBeInterviewed(): boolean {
      return (
        this.profileAccountData?.viewerHasMessaged &&
        (this.profileAccountData?.profiles?.babysitter?.profileVisible ||
          this.profileAccountData?.profiles?.nanny?.profileVisible)
      );
    },
    sitterCanBeRecruited(): boolean {
      return this.jobRecruitInfo?.eligibleForRecruit && !this.jobRecruitInfo?.recruitedBySeeker && isChildCare(this.careType);
    },
    profileUpgradePath(): string {
      return '/upgrade/pricing?source=profile';
    },
    interviewRequestPath(): string {
      return `/interview-request/${this.profileAccountData?.id}/new`;
    },
    openConversationTo(): { path: string; query: Record<string, string> } {
      return { path: '/messages', query: { uuid: this.profileAccountData?.id } };
    },
    newConversationTo(): { path: string; query: Record<string, string> } {
      return {
        path: '/messages/new',
        query: {
          uuid: this.profileAccountData?.id || '',
          displayName: this.profileAccountData?.displayName || '',
          return_path: window.location.pathname || '',
          source: 'profile',
        },
      };
    },
    inviteToApplyTo(): { path: string; query: Record<string, string> } {
      return { ...this.newConversationTo, query: { ...this.newConversationTo.query, checkAvailability: 'true' } };
    },
    contactButtons() {
      const buttons = [] as any[];

      if (this.sitterCanBeInterviewed) {
        const interviewButton: any = {
          value: 'Interview',
          variant: 'outlined',
        };
        if (this.isBasicParent) {
          interviewButton.to = this.profileUpgradePath;
        } else {
          interviewButton.to = this.interviewRequestPath;
        }
        buttons.push(interviewButton);
      }

      const messageButton: any = {
        value: this.isPremiumParent && this.profileAccountData?.viewerHasMessaged ? 'Open Conversation' : 'Message',
        variant: 'outlined',
      };
      if (this.isBasicParent) {
        messageButton.to = this.profileUpgradePath;
      } else {
        messageButton.to = this.profileAccountData?.viewerHasMessaged ? this.openConversationTo : this.newConversationTo;
      }
      buttons.push(messageButton);

      if (this.sitterCanBeRecruited) {
        buttons.push({
          value: 'Invite to Apply',
          to: this.inviteToApplyTo,
          variant: 'outlined',
        });
      }

      buttons[buttons.length - 1].variant = 'primary';
      if (this.showPrimaryOnly) {
        return [buttons[buttons.length - 1]];
      }

      return buttons;
    },
  },
  mounted() {
    if (!this.accountTypeInfo?.isParent || !this.profileAccountData?.id) {
      this.loading = false;
    }

    if (this.profileAccountData?.id) {
      this.fetchJobRecruit();
    }
  },
  methods: {
    fetchJobRecruit() {
      if (this.accountTypeInfo?.isParent) {
        this.$apollo
          .query({
            query: JOB_RECRUIT,
            variables: { providerId: this.profileAccountData?.id },
          })
          .then(({ data }) => {
            this.jobRecruitInfo = data.jobRecruit;
            this.loading = false;
          })
          .catch((error) => {
            console.error('Error fetching job recruit info:', error);
            this.loading = false;
          });
      }
    },
    handleButtonClick(button) {
      if (button.value === 'Invite to Apply') {
        // Keep event name stable for existing analytics dependencies.
        sendSegmentEvent('Check Availability Clicked', {
          sitter_uuid: this.profileAccountData?.id,
          care_type: this.careType,
        });
      } else if (button.value === 'Message' || button.value === 'Open Conversation') {
        sendSegmentEvent('Message Sitter Clicked', {
          sitter_uuid: this.profileAccountData?.id,
          care_type: this.careType,
        });
      } else if (button.value === 'Interview') {
        this.$store.dispatch('interviewRequest/setSitterUuid', this.profileAccountData?.id);
        sendSegmentEvent('Interview Sitter Clicked', {
          sitter_uuid: this.profileAccountData?.id,
          care_type: this.careType,
        });
      }
    },
  },
};
</script>
